import { t } from 'i18next';
import { FormProvider } from 'react-hook-form';
import { Box, Button, Tab } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CustomModal, SaveChangesModal } from '@components';
import { theme } from '@store';
import { useEditInsuranceModal } from './hooks/useEditInsuranceModal';
import { MedicareInsuranceList, OtherInsuranceList } from './lists';

interface IEditInsuranceProps {
  patient_id: string;
  disabled: boolean;
}

export const EditInsurance = ({
  patient_id,
  disabled,
}: IEditInsuranceProps) => {
  const {
    isOpen,
    handleOpen,
    handleClose,
    tabIndex,
    handleChangeTab,
    isMedicareFilled,
    isPrimaryOtherFilled,
    isSecondaryFilled,
    medicareFormMethods,
    primaryOtherFormMethods,
    secondaryFormMethods,
    currentIsSubmitting,
    medicareSaveModalOpen,
    primaryOtherSaveModalOpen,
    secondarySaveModalOpen,
    closeSaveModalOpen,
    handleMedicareSave,
    handleMedicareDiscard,
    handleMedicareModalClose,
    handlePrimaryOtherSave,
    handlePrimaryOtherDiscard,
    handlePrimaryOtherModalClose,
    handleSecondarySave,
    handleSecondaryDiscard,
    handleSecondaryModalClose,
    handleCloseSave,
    handleCloseDiscard,
    handleCloseModalClose,
    handleDeleteEntry,
    handleDeleteSecondaryEntry,
    handleClearMedicare,
    handleSubmitPrimaryOtherEntry,
    handleSubmitSecondaryEntry,
    handleSubmitMedicare,
  } = useEditInsuranceModal(patient_id);

  return (
    <>
      <Button
        disabled={disabled}
        onClick={handleOpen}
        sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 'unset' }}
      >
        EDIT
      </Button>

      <CustomModal
        isOpen={isOpen && !disabled}
        onClose={handleClose}
        title={t('patient.detailsCard.editPrimaryInsurance.title')}
      >
        <TabContext value={tabIndex}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <TabList onChange={handleChangeTab}>
              <Tab
                label='Primary (Medicare)'
                disabled={disabled}
                value='1'
                icon={
                  <CheckCircleIcon
                    htmlColor={theme.palette.success.main}
                    sx={{ display: isMedicareFilled ? 'block' : 'none' }}
                  />
                }
                iconPosition='start'
              />
              <Tab
                label='Primary (Other)'
                disabled={disabled}
                value='2'
                icon={
                  <CheckCircleIcon
                    htmlColor={theme.palette.success.main}
                    sx={{ display: isPrimaryOtherFilled ? 'block' : 'none' }}
                  />
                }
                iconPosition='start'
              />
              <Tab
                label='Secondary'
                disabled={disabled}
                value='3'
                icon={
                  <CheckCircleIcon
                    htmlColor={theme.palette.success.main}
                    sx={{ display: isSecondaryFilled ? 'block' : 'none' }}
                  />
                }
                iconPosition='start'
              />
            </TabList>
          </Box>

          <TabPanel sx={{ padding: '0 !important' }} value='1'>
            <FormProvider {...medicareFormMethods}>
              <MedicareInsuranceList
                patient_id={patient_id}
                onDelete={handleClearMedicare}
                onSubmit={handleSubmitMedicare}
              />
            </FormProvider>
          </TabPanel>

          <TabPanel sx={{ padding: '0 !important' }} value='2'>
            <FormProvider {...primaryOtherFormMethods}>
              <OtherInsuranceList
                insuranceIdLabel='Primary insurance ID'
                onDelete={handleDeleteEntry}
                onSubmitEntry={handleSubmitPrimaryOtherEntry}
                fieldKey='primary_other'
              />
            </FormProvider>
          </TabPanel>

          <TabPanel sx={{ padding: '0 !important' }} value='3'>
            <FormProvider {...secondaryFormMethods}>
              <OtherInsuranceList
                insuranceIdLabel='Secondary insurance ID'
                onDelete={handleDeleteSecondaryEntry}
                onSubmitEntry={handleSubmitSecondaryEntry}
                fieldKey='secondary'
              />
            </FormProvider>
          </TabPanel>
        </TabContext>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            position: 'sticky',
            bottom: 0,
            paddingY: 3,
            backgroundColor: theme.palette.common.white,
            zIndex: 3,
          }}
        >
          <Button
            variant='contained'
            color='secondary'
            disabled={currentIsSubmitting}
            onClick={handleClose}
          >
            Close
          </Button>
        </Box>

        <SaveChangesModal
          isOpen={medicareSaveModalOpen}
          onClose={handleMedicareModalClose}
          isValid={true}
          handleSaveChanges={handleMedicareSave}
          discardChanges={handleMedicareDiscard}
          isLoading={currentIsSubmitting}
        />

        <SaveChangesModal
          isOpen={primaryOtherSaveModalOpen}
          onClose={handlePrimaryOtherModalClose}
          isValid={true}
          handleSaveChanges={handlePrimaryOtherSave}
          discardChanges={handlePrimaryOtherDiscard}
          isLoading={currentIsSubmitting}
        />

        <SaveChangesModal
          isOpen={secondarySaveModalOpen}
          onClose={handleSecondaryModalClose}
          isValid={true}
          handleSaveChanges={handleSecondarySave}
          discardChanges={handleSecondaryDiscard}
          isLoading={currentIsSubmitting}
        />

        <SaveChangesModal
          isOpen={closeSaveModalOpen}
          onClose={handleCloseModalClose}
          isValid={true}
          handleSaveChanges={handleCloseSave}
          discardChanges={handleCloseDiscard}
          isLoading={currentIsSubmitting}
        />
      </CustomModal>
    </>
  );
};
